import { useSelection } from "@/contexts/SelectionContext";
import { useRestoreLessons } from "@/lib/hooks/lessons/useRestoreLessons";
import { useRestoreProjects } from "@/lib/hooks/projects/useRestoreProjects";
import { useRestoreSlides } from "@/lib/hooks/slides/useRestoreSlides";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";

interface RestoreAllModalProps {
  isOpen: boolean;
  modalType: "Slides" | "Projects" | "Lessons" | "Items";
  sourceId: string | null;
}

export function RestoreAllModal({ isOpen, modalType, sourceId }: RestoreAllModalProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    selectedProjectIds,
    selectedLessonIds,
    selectedSlideIds,
    clearAllSelections,
    toggleRestoreModal,
    toggleIsSelectionMode,
  } = useSelection();

  const {
    restoreProjects,
    loading: restoreProjectsLoading,
    error: restoreProjectsError,
  } = useRestoreProjects();
  const {
    restoreLessons,
    loading: restoreLessonsLoading,
    error: restoreLessonsError,
  } = useRestoreLessons();
  const {
    restoreSlides,
    loading: restoreSlidesLoading,
    error: restoreSlidesError,
  } = useRestoreSlides(sourceId);

  const handleClose = () => {
    setErrorMessage(null);
    clearAllSelections();
    toggleRestoreModal(false);
    toggleIsSelectionMode(false);
  };

  const handleRestoreAll = async () => {
    try {
      if (modalType === "Lessons" && selectedLessonIds.length > 0) {
        await restoreLessons(selectedLessonIds);
      }

      if (modalType === "Projects" && selectedProjectIds.length > 0) {
        await restoreProjects(selectedProjectIds);
      }

      if (modalType === "Slides" && selectedSlideIds.length > 0) {
        await restoreSlides(selectedSlideIds);
      }

      if (modalType === "Items") {
        if (selectedLessonIds.length > 0) {
          await restoreLessons(selectedLessonIds);
        }
        if (selectedProjectIds.length > 0) {
          await restoreProjects(selectedProjectIds);
        }
      }

      handleClose();
    } catch (error) {
      const message = (error as Error).message || "Failed to restore all items";
      setErrorMessage(message);
    }
  };

  // Compute itemCount based on modalType
  const getItemCount = (modalType: RestoreAllModalProps["modalType"]) =>
    modalType === "Lessons"
      ? selectedLessonIds.length
      : modalType === "Projects"
        ? selectedProjectIds.length
        : modalType === "Slides"
          ? selectedSlideIds.length
          : selectedLessonIds.length + selectedProjectIds.length;

  const getItemLabel = (modalType: RestoreAllModalProps["modalType"]) =>
    modalType.toLowerCase().replace(/s$/, "") + (getItemCount(modalType) !== 1 ? "s" : "");

  const isLoading = restoreProjectsLoading || restoreLessonsLoading || restoreSlidesLoading;
  const isError = !!(restoreProjectsError || restoreLessonsError || restoreSlidesError);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={errorMessage ? "Error !" : "Warning !"}
      size="sm"
      style="danger"
    >
      <div className="flex flex-col">
        {errorMessage ? (
          <>
            <p className="text-secondary-200 mb-6 text-center">{errorMessage}</p>
            <div className="flex justify-center">
              <Button onClick={handleClose} variant="secondary">
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-secondary-200 mb-6 text-center">
              {getItemCount(modalType)} {getItemLabel(modalType)} will be restored
            </p>

            <div className="flex gap-3 justify-between">
              <Button onClick={handleClose} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleRestoreAll} variant="danger" disabled={isLoading || isError}>
                {isLoading ? "Restore..." : "Restoring"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
